import { useEffect, useReducer, useState } from "react";
import { Share, X } from "lucide-react";
import { APP_NAME } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { canPrompt, installHint, isStandalone, onInstallChange, promptInstall } from "@/lib/install";
import { toast } from "@/lib/toast";

const DISMISSED = "install-prompt-dismissed";

/** Said once it is put off: the card goes for good, which a vanishing card alone does not say. */
export const PUT_OFF = "Won't ask again in this browser.";

/** Whether this browser was told "not now": a convenience, so a storage that throws just shows the card. */
function wasDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED) === "1";
  } catch {
    return false;
  }
}

/**
 * The card on Home that puts the app on a home screen (lib/install.ts):
 * Install where the browser offers it, the steps on an iPhone or iPad, and
 * nothing once it is installed or was put off. "Not now" is remembered in
 * this browser.
 */
export function InstallPrompt() {
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const [dismissed, setDismissed] = useState(wasDismissed);
  useEffect(() => onInstallChange(rerender), []);

  const hint = installHint({
    standalone: isStandalone(),
    userAgent: navigator.userAgent,
    touchPoints: navigator.maxTouchPoints ?? 0,
    prompt: canPrompt(),
  });
  if (dismissed || hint === "none") return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED, "1");
    } catch {
      // Remembered for this visit alone, so nothing is promised.
      return;
    }
    toast(PUT_OFF);
  };

  // An aside, drawn as shadcn's card.
  return (
    <Card role="complementary" aria-label="Install the app" size="sm" className="flex-row items-start gap-3 px-4">
      {/* In a box of its own: a card reads a picture first in it as a cover. */}
      <div className="shrink-0">
        <img src="/icons/icon-192.png" alt="" className="size-11 rounded-xl" />
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <p className="font-medium">Put {APP_NAME} on your home screen</p>
        {hint === "prompt" ? (
          <>
            <p className="text-muted-foreground">It opens full-screen, like an app.</p>
            <Button type="button" onClick={() => void promptInstall()}>
              Install
            </Button>
          </>
        ) : (
          <p className="text-muted-foreground">
            Tap <Share aria-label="Share" size={16} className="inline-block align-[-3px] text-foreground" /> Share, then{" "}
            <strong className="font-medium text-foreground">Add to Home Screen</strong>. It opens full-screen, like an
            app.
          </p>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={dismiss}
        aria-label="Not now"
        className="-mt-1.5 -mr-1.5 text-muted-foreground"
      >
        <X aria-hidden="true" className="size-5" />
      </Button>
    </Card>
  );
}
